// src/pages/problem-bank/ProblemBank.tsx - Complete version with Deletion Request Notifications

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { ProblemBankEntry, ProblemBankStatus } from '@/types/problemBank';
import {
  Search,
  Eye,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Building2,
  DollarSign,
  MapPin,
  MoreVertical,
  RefreshCw,
  FileText,
  TrendingUp,
  Zap,
  Package,
  Truck,
  Settings as SettingsIcon,
  PlusCircle,
  BarChart3,
  UserCheck,
  Calendar,
  Target,
  ChevronRight,
  Loader2,
  Filter,
  Shield,
  UserCog,
  X,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

const STATUS_COLORS: Record<ProblemBankStatus, { bg: string; text: string; label: string }> = {
  lead: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Lead' },
  prospect: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Prospect' },
  verified: { bg: 'bg-green-100', text: 'text-green-700', label: 'Verified' },
  assigned: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Assigned' },
  solved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Solved' }
};

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

export default function ProblemBank() {
  const navigate = useNavigate();
  const { user, allUsers } = useAuth();
  const { toast } = useToast();
  
  // Determine user role type
  const isProblemBankAnalyst = user?.analyst_type === 'problem_bank' || user?.role === 'senior_analyst';
  const isSeniorAnalyst = user?.role === 'senior_analyst';
  const isBusinessAnalyst = user?.analyst_type === 'business';
  
  const [problems, setProblems] = useState<ProblemBankEntry[]>([]);
  const [filteredProblems, setFilteredProblems] = useState<ProblemBankEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    assigned: 0,
    solved: 0,
    critical: 0,
    totalLoss: 0
  });

  // Assignment dialog state (only for senior analysts)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState<ProblemBankEntry | null>(null);
  const [selectedAnalystId, setSelectedAnalystId] = useState<string>('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [targetDate, setTargetDate] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Progress dialog state
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedAssignedProblem, setSelectedAssignedProblem] = useState<ProblemBankEntry | null>(null);

  // Deletion request dialog state (for Problem Bank Analysts)
  const [deleteRequestDialogOpen, setDeleteRequestDialogOpen] = useState(false);
  const [deleteProblem, setDeleteProblem] = useState<ProblemBankEntry | null>(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

  // Deletion approval dialog state (for Senior Analysts)
  const [deleteApprovalDialogOpen, setDeleteApprovalDialogOpen] = useState(false);
  const [deleteApprovalProblem, setDeleteApprovalProblem] = useState<ProblemBankEntry | null>(null);
  const [deleteApprovalNotes, setDeleteApprovalNotes] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Get analysts (excluding senior analysts for assignment)
  const analysts = allUsers.filter(u => u.role === 'analyst' && u.analyst_type === 'business');

  useEffect(() => {
    loadProblems();
  }, []);

  useEffect(() => {
    filterProblems();
  }, [problems, searchQuery, statusFilter, priorityFilter, activeTab]);

  const loadProblems = async () => {
    setIsLoading(true);
    try {
      const { data: problemsData, error: problemsError } = await supabase
        .from('problem_bank')
        .select('*')
        .order('created_at', { ascending: false });

      if (problemsError) throw problemsError;

      const parsedProblems = (problemsData || []).map(problem => ({
        ...problem,
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        rootCauseAnalysis: typeof problem.root_cause_analysis === 'string' ? JSON.parse(problem.root_cause_analysis) : problem.rootCauseAnalysis,
        solutionPotential: typeof problem.solution_potential === 'string' ? JSON.parse(problem.solution_potential) : problem.solutionPotential,
        verification: typeof problem.verification === 'string' ? JSON.parse(problem.verification) : problem.verification,
        priorityScore: problem.priority_score,
        assignment: typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment,
        outcome: typeof problem.outcome === 'string' ? JSON.parse(problem.outcome) : problem.outcome,
        problemId: problem.problem_id
      }));

      setProblems(parsedProblems);
      setFilteredProblems(parsedProblems);
      
      const totalLoss = parsedProblems.reduce((sum, p) => sum + (p.quantification?.totalLossPerYear || 0), 0);
      setStats({
        total: parsedProblems.length,
        verified: parsedProblems.filter(p => p.status === 'verified').length,
        assigned: parsedProblems.filter(p => p.status === 'assigned').length,
        solved: parsedProblems.filter(p => p.status === 'solved').length,
        critical: parsedProblems.filter(p => p.priorityScore?.priorityTier === 'critical').length,
        totalLoss: totalLoss
      });
      
    } catch (error) {
      console.error('Failed to load problems:', error);
      toast({
        title: 'Error',
        description: 'Failed to load problems',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterProblems = () => {
    let filtered = [...problems];

    if (activeTab === 'verified') {
      filtered = filtered.filter(p => p.status === 'verified');
    } else if (activeTab === 'assigned') {
      filtered = filtered.filter(p => p.status === 'assigned');
    } else if (activeTab === 'high-priority') {
      filtered = filtered.filter(p => p.priorityScore?.priorityTier === 'critical' || p.priorityScore?.priorityTier === 'high');
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title?.toLowerCase().includes(query) ||
        p.problemId?.toLowerCase().includes(query) ||
        p.anchor?.name?.toLowerCase().includes(query) ||
        p.anchor?.location?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(p => p.priorityScore?.priorityTier === priorityFilter);
    }

    setFilteredProblems(filtered);
  };

  // Handle deletion request (for Problem Bank Analysts)
  const handleRequestDeletion = async () => {
    if (!deleteProblem || !user) return;
    
    setIsSubmittingRequest(true);
    try {
      // Create deletion request
      const { data: request, error: requestError } = await supabase
        .from('problem_deletion_requests')
        .insert({
          problem_id: deleteProblem.id,
          problem_title: deleteProblem.title,
          requested_by: user.id,
          requested_by_name: user.name,
          reason: deletionReason,
          status: 'pending'
        })
        .select()
        .single();
      
      if (requestError) throw requestError;
      
      // Get all senior analysts to notify
      const { data: seniorAnalysts, error: seniorError } = await supabase
        .from('users')
        .select('id, name')
        .eq('role', 'senior_analyst');
      
      if (!seniorError && seniorAnalysts && seniorAnalysts.length > 0) {
        // Create notifications for each senior analyst
        for (const senior of seniorAnalysts) {
          await supabase
            .from('notifications')
            .insert({
              user_id: senior.id,
              title: 'Problem Deletion Request',
              message: `${user.name} requested to delete problem: "${deleteProblem.title}". Reason: ${deletionReason.substring(0, 100)}${deletionReason.length > 100 ? '...' : ''}`,
              type: 'warning',
              link: `/admin/assignments?tab=deletion-requests`,
              read: false,
              created_at: new Date().toISOString()
            });
        }
      }
      
      toast({
        title: 'Deletion Request Submitted',
        description: `Your request to delete "${deleteProblem.title}" has been sent to Senior Analysts.`,
      });
      
      setDeleteRequestDialogOpen(false);
      setDeleteProblem(null);
      setDeletionReason('');
      
    } catch (error) {
      console.error('Error submitting deletion request:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit deletion request',
        variant: 'destructive'
      });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // Handle direct deletion (for Senior Analysts)
  const handleDeleteProblem = async () => {
    if (!deleteApprovalProblem) return;
    
    setIsDeleting(true);
    try {
      console.log('🗑️ Attempting to delete problem:', deleteApprovalProblem.id);
      
      const { data, error: deleteError } = await supabase
        .from('problem_bank')
        .delete()
        .eq('id', deleteApprovalProblem.id)
        .select();
      
      if (deleteError) {
        console.error('❌ Delete error:', deleteError);
        throw deleteError;
      }
      
      console.log('✅ Delete successful:', data);
      
      toast({
        title: 'Problem Deleted',
        description: `"${deleteApprovalProblem.title}" has been permanently deleted.`,
      });
      
      setDeleteApprovalDialogOpen(false);
      setDeleteApprovalProblem(null);
      setDeleteApprovalNotes('');
      loadProblems(); // Refresh the list
      
    } catch (error) {
      console.error('Error deleting problem:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete problem',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAssignClick = (problem: ProblemBankEntry) => {
    setSelectedProblem(problem);
    setSelectedAnalystId('');
    setAssignmentNotes('');
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 30);
    setTargetDate(defaultDate.toISOString().split('T')[0]);
    setAssignDialogOpen(true);
  };

  const handleAssignProblem = async () => {
    if (!selectedProblem || !selectedAnalystId) return;

    setIsAssigning(true);
    try {
      const analyst = analysts.find(a => a.id === selectedAnalystId);
      
      const assignment = {
        squadId: `analyst-${selectedAnalystId}`,
        squadName: analyst?.name,
        members: [{
          role: 'analyst' as const,
          name: analyst?.name || 'Analyst',
          userId: selectedAnalystId
        }],
        skillsMatch: 4,
        assignmentDate: new Date().toISOString(),
        targetDeliveryDate: targetDate,
        targetLOIDate: new Date(new Date(targetDate).setMonth(new Date(targetDate).getMonth() + 2)).toISOString(),
        currentPhase: 'discovery' as const,
        milestones: [
          {
            description: 'Initial problem assessment',
            targetDate: new Date(new Date(targetDate).setDate(new Date(targetDate).getDate() - 28)).toISOString(),
            completed: false
          },
          {
            description: 'Site visit and data collection',
            targetDate: new Date(new Date(targetDate).setDate(new Date(targetDate).getDate() - 21)).toISOString(),
            completed: false
          },
          {
            description: 'Solution hypothesis development',
            targetDate: new Date(new Date(targetDate).setDate(new Date(targetDate).getDate() - 14)).toISOString(),
            completed: false
          },
          {
            description: 'LOI presentation',
            targetDate: new Date(new Date(targetDate).setDate(new Date(targetDate).getDate() - 7)).toISOString(),
            completed: false
          },
          {
            description: 'Final delivery',
            targetDate: targetDate,
            completed: false
          }
        ],
        weeklyCheckInLink: `https://meet.google.com/assign-${selectedProblem.id.substring(0, 6)}`,
        notes: assignmentNotes,
        assignedBy: user?.id,
        assignedByName: user?.name
      };

      const { error: updateError } = await supabase
        .from('problem_bank')
        .update({
          status: 'assigned',
          assignment: JSON.stringify(assignment),
          assigned_analyst_id: selectedAnalystId,
          assigned_analyst_name: analyst?.name,
          assigned_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedProblem.id);

      if (updateError) throw updateError;

      toast({
        title: 'Problem Assigned',
        description: `${selectedProblem.title} assigned to ${analyst?.name}`,
      });

      setAssignDialogOpen(false);
      setSelectedProblem(null);
      loadProblems();
    } catch (error) {
      console.error('Assignment failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign problem',
        variant: 'destructive'
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const calculateProgress = (problem: ProblemBankEntry): number => {
    if (!problem.assignment?.milestones) return 0;
    const completed = problem.assignment.milestones.filter(m => m.completed).length;
    return Math.round((completed / problem.assignment.milestones.length) * 100);
  };

  const getDaysRemaining = (problem: ProblemBankEntry): number => {
    if (!problem.assignment?.targetDeliveryDate) return 0;
    const target = new Date(problem.assignment.targetDeliveryDate);
    const now = new Date();
    const diffTime = target.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getAssignedAnalystName = (problem: ProblemBankEntry): string => {
    if (problem.assigned_analyst_name) return problem.assigned_analyst_name;
    if (!problem.assignment?.members) return 'Unassigned';
    const analyst = problem.assignment.members.find(m => m.role === 'analyst');
    const analystUser = allUsers.find(u => u.id === analyst?.userId);
    return analystUser?.name || analyst?.name || 'Unknown';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getCategoryIcon = (category: string) => {
    switch(category) {
      case 'downtime': return <Clock className="h-4 w-4" />;
      case 'energy_waste': return <Zap className="h-4 w-4" />;
      case 'inventory_shrinkage': return <Package className="h-4 w-4" />;
      case 'quality_control': return <CheckCircle className="h-4 w-4" />;
      case 'logistics': return <Truck className="h-4 w-4" />;
      case 'maintenance': return <SettingsIcon className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  // Business Analysts should not access Problem Bank
  if (isBusinessAnalyst) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground text-center max-w-md">
          Problem Bank is only accessible to Problem Bank Analysts and Senior Analysts.
          Please contact your administrator if you need access.
        </p>
        <Button className="mt-6" onClick={() => navigate('/analyst-dashboard')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Problem Bank</h1>
          <p className="text-muted-foreground mt-1">
            Browse and analyze industrial problems • {stats.total} total • {formatCurrency(stats.totalLoss)} verified loss
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadProblems}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {(isProblemBankAnalyst || isSeniorAnalyst) && (
            <Button variant="outline" onClick={() => navigate('/problem-bank/submissions')}>
              <PlusCircle className="h-4 w-4 mr-2" />
              View Submissions
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Problems</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{stats.total}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Verified</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold text-green-600">{stats.verified}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Assigned</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold text-purple-600">{stats.assigned}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Critical Priority</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold text-red-600">{stats.critical}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Solved</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold text-emerald-600">{stats.solved}</div></CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="all">All Problems</TabsTrigger>
          <TabsTrigger value="verified">Ready to Assign</TabsTrigger>
          <TabsTrigger value="assigned">Currently Assigned</TabsTrigger>
          <TabsTrigger value="high-priority">High Priority</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, company, ID, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="solved">Solved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Problems Table */}
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">ID</TableHead>
                    <TableHead>Problem</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead className="text-right">Loss (KES/year)</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-8 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                      </TableRow>
                    ))
                  ) : filteredProblems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-muted-foreground">No problems found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProblems.map((problem) => {
                      const progress = calculateProgress(problem);
                      const daysRemaining = getDaysRemaining(problem);
                      const isOverdue = problem.status === 'assigned' && daysRemaining < 0;
                      const assignedTo = getAssignedAnalystName(problem);
                      
                      return (
                        <TableRow key={problem.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/problem-bank/problem/${problem.id}`)}>
                          <TableCell className="font-mono text-xs">{problem.problemId}</TableCell>
                          <TableCell className="font-medium max-w-[250px] truncate">{problem.title}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1"><Building2 className="h-3 w-3 text-muted-foreground" /><span className="text-sm truncate max-w-[120px]">{problem.anchor?.name || 'Unknown'}</span></div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /><span className="truncate max-w-[120px]">{problem.anchor?.location || 'Unknown'}</span></div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={STATUS_COLORS[problem.status]?.bg + ' ' + STATUS_COLORS[problem.status]?.text}>
                              {STATUS_COLORS[problem.status]?.label || problem.status}
                            </Badge>
                            {isOverdue && <Badge variant="destructive" className="ml-2">Overdue</Badge>}
                          </TableCell>
                          <TableCell>
                            {problem.priorityScore ? (
                              <Badge variant="outline" className={PRIORITY_COLORS[problem.priorityScore.priorityTier]}>
                                {problem.priorityScore.priorityTier.toUpperCase()}
                              </Badge>
                            ) : <Badge variant="outline">Not scored</Badge>}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600 whitespace-nowrap">
                            {formatCurrency(problem.quantification?.totalLossPerYear || 0)}
                          </TableCell>
                          <TableCell>
                            {problem.status === 'assigned' ? (
                              <div className="flex items-center gap-2">
                                <span className="text-sm truncate max-w-[120px]">{assignedTo}</span>
                              </div>
                            ) : problem.status === 'verified' ? (
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 whitespace-nowrap">Ready for Assignment</Badge>
                            ) : <span className="text-muted-foreground text-sm">-</span>}
                          </TableCell>
                          <TableCell className="min-w-[130px]">
                            {problem.status === 'assigned' ? (
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs"><span>{progress}%</span><span className={isOverdue ? 'text-red-600' : 'text-muted-foreground'}>{daysRemaining > 0 ? `${daysRemaining}d left` : `${Math.abs(daysRemaining)}d overdue`}</span></div>
                                <Progress value={progress} className="h-1.5" />
                              </div>
                            ) : <span className="text-muted-foreground text-sm">Not started</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {problem.category?.slice(0, 2).map((cat, i) => (<div key={i} className="text-muted-foreground" title={cat}>{getCategoryIcon(cat)}</div>))}
                              {problem.category?.length > 2 && <span className="text-xs text-muted-foreground">+{problem.category.length - 2}</span>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              {problem.status === 'verified' && isSeniorAnalyst && (
                                <Button variant="default" size="sm" onClick={() => handleAssignClick(problem)} className="bg-green-600 hover:bg-green-700 h-8">
                                  <UserCheck className="h-3 w-3 mr-1" />Assign
                                </Button>
                              )}
                              {problem.status === 'assigned' && (
                                <Button variant="outline" size="sm" onClick={() => { setSelectedAssignedProblem(problem); setProgressDialogOpen(true); }} className="h-8">
                                  <BarChart3 className="h-3 w-3 mr-1" />Progress
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/problem-bank/problem/${problem.id}`)}><Eye className="h-4 w-4" /></Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => navigate(`/problem-bank/problem/${problem.id}`)}><Eye className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
                                  <DropdownMenuItem><Download className="h-4 w-4 mr-2" />Download Dossier</DropdownMenuItem>
                                  
                                  {/* Delete Option - Different for Senior Analysts vs Problem Bank Analysts */}
                                  {isSeniorAnalyst ? (
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setDeleteApprovalProblem(problem);
                                        setDeleteApprovalDialogOpen(true);
                                      }}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />Delete Problem
                                    </DropdownMenuItem>
                                  ) : isProblemBankAnalyst ? (
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setDeleteProblem(problem);
                                        setDeleteRequestDialogOpen(true);
                                      }}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />Request Deletion
                                    </DropdownMenuItem>
                                  ) : null}
                                  
                                  {problem.status === 'verified' && (
                                    <><DropdownMenuSeparator /><DropdownMenuItem><BarChart3 className="h-4 w-4 mr-2" />Calculate Priority Score</DropdownMenuItem></>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assignment Dialog - Only for Senior Analysts */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Problem to Analyst</DialogTitle>
            <DialogDescription>Assign this verified problem to an analyst for investigation and solution development.</DialogDescription>
          </DialogHeader>
          {selectedProblem && (
            <div className="space-y-6">
              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Problem Summary</h4>
                <p className="text-sm font-semibold">{selectedProblem.title}</p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="text-red-600">{formatCurrency(selectedProblem.quantification?.totalLossPerYear || 0)}/yr</span>
                  <span>{selectedProblem.anchor?.name}</span><span>{selectedProblem.anchor?.location}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{selectedProblem.quantification?.description}</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Select Analyst *</Label>
                  <Select value={selectedAnalystId} onValueChange={setSelectedAnalystId}>
                    <SelectTrigger><SelectValue placeholder="Choose an analyst" /></SelectTrigger>
                    <SelectContent>
                      {analysts.map(analyst => (
                        <SelectItem key={analyst.id} value={analyst.id}>
                          <div className="flex items-center gap-2">
                            <span>{analyst.name}</span>
                            <Badge variant="outline" className="ml-2 text-xs">{analyst.email}</Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Target Delivery Date *</Label><Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} min={new Date().toISOString().split('T')[0]} /><p className="text-xs text-muted-foreground">Recommended: 30-45 days from assignment</p></div>
                <div className="space-y-2"><Label>Assignment Notes (Optional)</Label><Textarea placeholder="Add any specific instructions or context for the analyst..." value={assignmentNotes} onChange={(e) => setAssignmentNotes(e.target.value)} rows={3} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignProblem} disabled={!selectedAnalystId || !targetDate || isAssigning} className="bg-green-600 hover:bg-green-700">
              {isAssigning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Assigning...</> : <><UserCheck className="h-4 w-4 mr-2" />Assign Problem</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Dialog */}
      <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-background z-10 pb-4 border-b">
            <DialogTitle>Problem Progress</DialogTitle>
            <DialogDescription>Track progress on assigned problem</DialogDescription>
          </DialogHeader>
          {selectedAssignedProblem && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg">{selectedAssignedProblem.title}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={STATUS_COLORS[selectedAssignedProblem.status]?.bg + ' ' + STATUS_COLORS[selectedAssignedProblem.status]?.text}>
                    {selectedAssignedProblem.status.toUpperCase()}
                  </Badge>
                  {selectedAssignedProblem.priorityScore && (
                    <Badge className={PRIORITY_COLORS[selectedAssignedProblem.priorityScore.priorityTier]}>
                      {selectedAssignedProblem.priorityScore.priorityTier.toUpperCase()}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <UserCheck className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Assigned to: {getAssignedAnalystName(selectedAssignedProblem)}</p>
                    {selectedAssignedProblem.assigned_at && (
                      <p className="text-sm text-muted-foreground">Assigned on: {new Date(selectedAssignedProblem.assigned_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-muted/30 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Overall Progress</span>
                  <span className="text-lg font-bold">{calculateProgress(selectedAssignedProblem)}%</span>
                </div>
                <Progress value={calculateProgress(selectedAssignedProblem)} className="h-3" />
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Assigned</p>
                    <p className="font-medium text-sm">{selectedAssignedProblem.assignment?.assignmentDate ? new Date(selectedAssignedProblem.assignment.assignmentDate).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Target</p>
                    <p className="font-medium text-sm">{selectedAssignedProblem.assignment?.targetDeliveryDate ? new Date(selectedAssignedProblem.assignment.targetDeliveryDate).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Days Remaining</p>
                    <p className={`font-medium text-sm ${getDaysRemaining(selectedAssignedProblem) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {getDaysRemaining(selectedAssignedProblem)} days
                    </p>
                  </div>
                </div>
              </div>
              
              {selectedAssignedProblem.assignment?.milestones && (
                <div>
                  <h4 className="font-medium mb-3">Milestones</h4>
                  <div className="space-y-3">
                    {selectedAssignedProblem.assignment.milestones.map((milestone, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                        {milestone.completed ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{milestone.description}</p>
                          <p className="text-sm text-muted-foreground">Target: {new Date(milestone.targetDate).toLocaleDateString()}</p>
                        </div>
                        <Badge variant="outline" className={milestone.completed ? 'bg-green-50 text-green-700 flex-shrink-0' : 'bg-yellow-50 text-yellow-700 flex-shrink-0'}>
                          {milestone.completed ? 'Completed' : 'Pending'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedAssignedProblem.assignment?.weeklyCheckInLink && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-700">Weekly Check-in</span>
                  </div>
                  <a 
                    href={selectedAssignedProblem.assignment.weeklyCheckInLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    Join weekly meeting
                    <ChevronRight className="h-3 w-3" />
                  </a>
                </div>
              )}
              
              {selectedAssignedProblem.assignment?.notes && (
                <div>
                  <h4 className="font-medium mb-2">Assignment Notes</h4>
                  <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg whitespace-pre-wrap">
                    {selectedAssignedProblem.assignment.notes}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="sticky bottom-0 bg-background pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setProgressDialogOpen(false)}>Close</Button>
            <Button onClick={() => navigate(`/problem-bank/problem/${selectedAssignedProblem?.id}`)}>
              <Eye className="h-4 w-4 mr-2" />View Full Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Request Dialog - For Problem Bank Analysts */}
      <Dialog open={deleteRequestDialogOpen} onOpenChange={setDeleteRequestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Problem Deletion</DialogTitle>
            <DialogDescription>
              Please provide a reason for deleting this problem. A Senior Analyst will review your request.
            </DialogDescription>
          </DialogHeader>
          
          {deleteProblem && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="font-medium">{deleteProblem.title}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Problem ID: {deleteProblem.problemId}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Deletion *</Label>
                <Textarea
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  placeholder="Explain why this problem should be deleted..."
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRequestDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestDeletion} 
              disabled={!deletionReason.trim() || isSubmittingRequest}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmittingRequest ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Submit Deletion Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog - For Senior Analysts */}
      <Dialog open={deleteApprovalDialogOpen} onOpenChange={setDeleteApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Problem</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this problem? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deleteApprovalProblem && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="font-medium">{deleteApprovalProblem.title}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Problem ID: {deleteApprovalProblem.problemId}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Status: {deleteApprovalProblem.status}
                </p>
                <p className="text-sm text-muted-foreground">
                  Annual Loss: {formatCurrency(deleteApprovalProblem.quantification?.totalLossPerYear || 0)}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Deletion (Optional)</Label>
                <Textarea
                  value={deleteApprovalNotes}
                  onChange={(e) => setDeleteApprovalNotes(e.target.value)}
                  placeholder="Note why this problem is being deleted..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteProblem} 
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}